import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  Grid,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { Delete } from "@mui/icons-material";
import {
  fetchOnePromoBanner,
  addEditPromoBanner,
  deletePromoBanner,
} from "./../../actions/promoBannerActions";
import { uploadImage, clearMedia } from "./../../actions/mediaActions";
import Select from "react-select";
import Swal from "sweetalert2";
import ImageContainerPreview from "../Common/ImageContainerPreview";
import { preventEnterFormSubmission } from '../../utils/formUtils';

// import "./AddEditPromoBanner.css";

const AddEditPromoBanner = forwardRef(({ isEdit, selectedId }, ref) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    promo_banner_url: "",
    promo_banner_title: "",
    promo_banner_text: "",
    promo_banner_is_active: 0,
    promo_banner_icon: "",
    promo_banner_image_wide: "",
    is_winback: 0,
  });

  const [previewData, setPreviewData] = useState({
    isSetToEdit: false,
    bannerPreview: null,
    iconPreview: null,
    wideBannerPreview: null,
  });

  useImperativeHandle(ref, () => ({
    validateAndGetData: () => {
      if (formData.promo_banner_url === "") {
        Swal.fire({
          icon: "error",
          text: "You must upload a Promo Banner image.",
        });
        return false;
      }
      return formData;
    }
  }));

  useEffect(() => {
    if (!isEdit || !selectedId || previewData.isSetToEdit) return;

    setLoading(true);
    dispatch(fetchOnePromoBanner(selectedId))
      .then(bannerData => {
        setFormData({
          ...bannerData,
        });
        setPreviewData(prev => ({ ...prev, isSetToEdit: true }));
      })
      .catch(() => {
        Swal.fire({
          icon: "warning",
          text: "Error finding selected Promo Banner to edit",
        });
      })
      .finally(() => setLoading(false));
  }, [dispatch, isEdit, selectedId, previewData.isSetToEdit]);

  // Keep a ref to the latest previewData so the unmount cleanup always reads
  // the current blob URLs without re-subscribing on every change (which would
  // revoke still-in-use URLs via stale closures in the previous cleanup).
  const previewDataRef = useRef(previewData);
  previewDataRef.current = previewData;

  useEffect(() => {
    return () => {
      const { bannerPreview, iconPreview, wideBannerPreview } = previewDataRef.current;
      if (bannerPreview) window.URL.revokeObjectURL(bannerPreview);
      if (iconPreview) window.URL.revokeObjectURL(iconPreview);
      if (wideBannerPreview) window.URL.revokeObjectURL(wideBannerPreview);
      dispatch(clearMedia());
    };
  }, [dispatch]);

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleBannerImageUpload = (e) => {
    const file = e.target.files[0];
    if (previewData.bannerPreview) {
      window.URL.revokeObjectURL(previewData.bannerPreview);
    }
    setPreviewData(prev => ({
      ...prev,
      bannerPreview: window.URL.createObjectURL(file),
    }));
    dispatch(uploadImage(file))
      .then((response) => {
        setFormData(prev => ({
          ...prev,
          promo_banner_url: response.data.image_url,
        }));
        dispatch(clearMedia());
      });
  };

  const handleRemoveImage = (formField, previewField) => () => {
    setPreviewData(prev => {
      if (prev[previewField]) {
        window.URL.revokeObjectURL(prev[previewField]);
      }
      return { ...prev, [previewField]: null };
    });
    setFormData(prev => ({ ...prev, [formField]: "" }));
  };

  const handleSwitchChange = (e) => {
    const { name, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: checked,
    }));
  };

  const handleWideBannerImageUpload = (e) => {
    if (!e || !e.target.files[0]) return false;

    const file = e.target.files[0];
    if (previewData.wideBannerPreview) {
      window.URL.revokeObjectURL(previewData.wideBannerPreview);
    }
    setPreviewData(prev => ({
      ...prev,
      wideBannerPreview: window.URL.createObjectURL(file),
    }));
    dispatch(uploadImage(file))
      .then((response) => {
        setFormData(prev => ({
          ...prev,
          promo_banner_image_wide: response.data.image_url,
        }));
        dispatch(clearMedia());
      });
  };

  const handleIconImageUpload = (e) => {
    if (!e || !e.target.files[0]) return false;

    const file = e.target.files[0];
    if (previewData.iconPreview) {
      window.URL.revokeObjectURL(previewData.iconPreview);
    }
    setPreviewData(prev => ({
      ...prev,
      iconPreview: window.URL.createObjectURL(file),
    }));
    dispatch(uploadImage(file))
      .then((response) => {
        setFormData(prev => ({
          ...prev,
          promo_banner_icon: response.data.image_url,
        }));
        dispatch(clearMedia());
      });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    Swal.fire({
      title: "Saving...",
      animation: false,
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
    });

    dispatch(addEditPromoBanner(JSON.stringify(formData), () => navigate('/banners')));
  };

  const handleDelete = (e) => {
    e.preventDefault();
    Swal.fire({
      icon: "warning",
      text: "Are you sure you want to delete this Banner?",
      showConfirmButton: true,
      showCancelButton: true,
      reverseButtons: true,
      confirmButtonText: "Delete Item",
    }).then((result) => {
      if (result.value) {
        dispatch(deletePromoBanner(selectedId, () => navigate('/banners')));
      }
    });
  };

  if (isEdit && loading) {
    return <h1>Loading...</h1>;
  }

  return (
    <Box component="form" id="promo-banner-form" onSubmit={handleSubmit} onKeyDown={preventEnterFormSubmission} autoComplete="off" sx={{ width: '100%' }}>
      <Box display="grid" gap={4}>
        {/* Banner Details Section */}
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Banner Details
          </Typography>
          <Box display="grid" gap={3} gridTemplateColumns={{ xs: '1fr', md: 'repeat(2, 1fr)' }}>
            <Box>
              <TextField
                fullWidth
                label="Banner Title"
                name="promo_banner_title"
                value={formData.promo_banner_title}
                onChange={handleFieldChange}
                variant="outlined"
              />
            </Box>
            <Box>
              <Box display="flex" gap={2} alignItems="center">
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!formData.promo_banner_is_active}
                      onChange={handleSwitchChange}
                      name="promo_banner_is_active"
                    />
                  }
                  label="Is Active"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!formData.is_winback}
                      onChange={handleSwitchChange}
                      name="is_winback"
                    />
                  }
                  label="Is Winback"
                />
              </Box>
            </Box>
            <Box gridColumn={{ xs: '1 / -1', md: '1 / -1' }}>
              <TextField
                fullWidth
                label="Banner Description"
                name="promo_banner_text"
                value={formData.promo_banner_text}
                onChange={handleFieldChange}
                variant="outlined"
                multiline
                rows={4}
              />
            </Box>
          </Box>
        </Box>

        {/* Banner Images Section */}
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Banner Images
          </Typography>
          <Box display="grid" gap={3} gridTemplateColumns={{ xs: '1fr', md: 'repeat(3, 1fr)' }}>
            <Box>
              <ImageContainerPreview
                title="Banner Image (Mobile)"
                image={previewData.bannerPreview || formData.promo_banner_url}
                name="promo_banner_url"
                handleRemove={handleRemoveImage("promo_banner_url", "bannerPreview")}
                handleImageUpload={handleBannerImageUpload}
                sx={{ padding: 1, background: '#FFFCF5', height: 90, objectFit: 'contain' }}
              />
            </Box>
            <Box>
              <ImageContainerPreview
                title="Banner Wide Image (Web)"
                image={previewData.wideBannerPreview || formData.promo_banner_image_wide}
                name="promo_banner_image_wide"
                handleRemove={handleRemoveImage("promo_banner_image_wide", "wideBannerPreview")}
                handleImageUpload={handleWideBannerImageUpload}
                sx={{ padding: 1, background: '#FFFCF5', height: 90, objectFit: 'contain' }}
              />
            </Box>
            <Box>
              <ImageContainerPreview
                title="Icon Image"
                image={previewData.iconPreview || formData.promo_banner_icon}
                name="promo_banner_icon"
                handleRemove={handleRemoveImage("promo_banner_icon", "iconPreview")}
                handleImageUpload={handleIconImageUpload}
                sx={{ padding: 1, background: '#364152', height: 250, objectFit: 'contain', width: 120, height: 120, mx: 'auto' }}
              />
            </Box>
          </Box>
        </Box>

        {isEdit && (
          <Box sx={{ p: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              color="error"
              onClick={handleDelete}
              startIcon={<Delete />}
              variant="contained"
            >
              Delete Banner
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
});

export default AddEditPromoBanner;
